<script setup>
import { ref, computed } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})


// Emit click so parent can handle lock/unlock just like TypeformCard/YouTubeCard
const emit = defineEmits(['click','share'])

const layout = computed(() => props.link?.options?.layout || 'classic')
const isFeatured = computed(() => layout.value === 'featured')
const isClassic  = computed(() => layout.value === 'classic')

// Facebook display mode: 'post_embed' or 'open' (defaults to 'post_embed')
const mode = computed(() => props.link?.options?.facebook?.display_mode || 'post_embed')
const isEmbed = computed(() => mode.value === 'post_embed')
const isOpen  = computed(() => mode.value === 'open')

const playing = ref(false)

function onRootClick(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
  emit('click', { link: props.link, mode: mode.value })
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

function togglePlay(){
  // For embed mode, local toggle of inline player; for link mode, delegate to parent lock flow
  if (!isEmbed.value) return onRootClick()
  playing.value = !playing.value
}
function start(e){
    e?.stopPropagation?.()

    // If post_embed → let parent check lock first
    if (isEmbed.value) {
        emit('click', { link: props.link, mode: mode.value, action: 'play' })
        return
    }

    // fallback (should not really happen)
    playing.value = true
}

defineExpose({
    // Backwards-compatible API for parent components to trigger inline playback
    playUrl() {
        alert('playUrl is deprecated. Use start instead.')
        playing.value = true;
    },
});
function generateFacebookEmbedUrl(url){
  if (!url) return '';

  // Handle https://www.facebook.com/watch/?v=1234567890
  const watchPattern = /facebook\.com\/watch\/?\?v=(\d+)/;
  const matchWatch = url.match(watchPattern);
  if (matchWatch && matchWatch[1]) {
    const videoId = matchWatch[1];
    return `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch/?v=${videoId}`;
  }

  // Handle https://www.facebook.com/username/videos/1234567890/
  const directPattern = /facebook\.com\/.*\/videos\/(\d+)/;
  const matchDirect = url.match(directPattern);
  if (matchDirect && matchDirect[1]) {
    const videoId = matchDirect[1];
    return `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch/?v=${videoId}`;
  }

  // Fallback for other Facebook URLs
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`;
}

const embedUrl = computed(() => {
  if (!isEmbed.value) return '';
  const url = props.link?.url || '';
  return generateFacebookEmbedUrl(url);
})

const thumbUrl = computed(()=>{
  const file = props.link?.file
  if (file) return buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, file)
  return ''
})
</script>

<template>
  <!-- FEATURED: modern Facebook-style box with overlays, consistent with YouTube card -->
  <GeneralEmbedButton v-if="isFeatured"
                      class="fb featured"
                      :link="link"
                      :appearance="appearance"
                      :buttonStyle="buttonStyle"
                      :DoBucket="DoBucket"
                      :DoEndpoint="DoEndpoint"
                      :layout="layout"
                      @click="onRootClick">

    <div class="box" :class="[appearance.buttons]">
      <!-- Link mode: open external -->
      <a v-if="isOpen" class="pane link" :href="link.url" rel="noopener nofollow" target="_blank" @click.prevent.stop="onRootClick">
        <img class="thumb" :src="thumbUrl" />
        <div class="shade" aria-hidden="true"></div>
        <div class="video-badge no-bg">
          <div class="title-row">
            <span class="brand-chip">
                <svg fill="#fff" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M21.95 5.005l-3.306-.004c-3.206 0-5.277 2.124-5.277 5.415v2.495H10.05v4.515h3.317l-.004 9.575h4.641l.004-9.575h3.806l-.003-4.514h-3.803v-2.117c0-1.018.241-1.533 1.566-1.533l2.366-.001.01-4.256z"></path></g></svg>
            </span>
            <CardTitle :title="link.title || 'Facebook'" layout="featured" align="left" />
          </div>
        </div>
        <button class="share" @click.prevent.stop="onShare" aria-label="Share">
          <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
                  d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
          </svg>
        </button>

      </a>

      <!-- Embed mode: thumb -> iframe -->
      <template v-else>
        <template v-if="!playing">
          <img class="thumb" :src="thumbUrl" />
          <div class="shade" aria-hidden="true"></div>
          <button class="play" @click.stop="start" aria-label="Play">
            <svg viewBox="0 0 60 60" width="40" height="40"><circle cx="28" cy="30" r="28" fill="#fff" opacity=".9"/><path d="M22 20 L38 30 L22 40 Z" fill="#111"/></svg>
          </button>
          <div class="video-badge no-bg">
            <div class="title-row">
              <span class="brand-chip">
                  <svg fill="#fff" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M21.95 5.005l-3.306-.004c-3.206 0-5.277 2.124-5.277 5.415v2.495H10.05v4.515h3.317l-.004 9.575h4.641l.004-9.575h3.806l-.003-4.514h-3.803v-2.117c0-1.018.241-1.533 1.566-1.533l2.366-.001.01-4.256z"></path></g></svg>
              </span>
              <CardTitle :title="link.title || 'Facebook'" layout="featured" align="left" />
            </div>
          </div>
          <button class="share" v-if="!playing" @click.stop="onShare" aria-label="Share">
            <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
                    d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
            </svg>
          </button>
        </template>
        <template v-else>
          <button class="close-btn" @click.stop="playing=false" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.42L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/>
            </svg>
          </button>
          <iframe v-if="embedUrl" :src="embedUrl" style="border:none; overflow:hidden;" scrolling="no" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowfullscreen></iframe>
        </template>
      </template>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC -->
  <GeneralButton v-else-if="isClassic"
                 class="fb classic"
                 :link="link"
                 :appearance="appearance"
                 :buttonStyle="buttonStyle"
                 :DoBucket="DoBucket"
                 :DoEndpoint="DoEndpoint"
                 :layout="layout"
                 :hideLeftImage="isEmbed && playing"
                 @click="isOpen ? onRootClick($event) : togglePlay()">
    <template #title>
      <CardTitle :title="link.title || 'Facebook'" layout="classic" align="center" :max="100" />
    </template>
    <template #actions>
      <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
      </svg>
    </template>

    <transition name="slide-fade">
      <div v-if="isEmbed && playing" class="classic-embed mt-2 w-100">
        <div class="box" :class="[appearance.buttons]">
          <button class="close-btn" @click.stop="playing=false" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.42L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/>
            </svg>
          </button>
          <iframe v-if="embedUrl" :src="embedUrl" style="border:none; overflow:hidden;" scrolling="no" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowfullscreen></iframe>
        </div>
      </div>
    </transition>
  </GeneralButton>
</template>

<style scoped>
/* Featured box (16:9) and overlays */
.fb.featured .box{position: relative; aspect-ratio:16/9; background:#000; overflow:hidden; border-radius:12px;}
.fb.featured .pane.link, .fb.featured .box iframe, .fb.featured .box .thumb{position:absolute; inset:0; width:100%; height:100%;}
.fb.featured .thumb{object-fit:cover; z-index:0;}
/* Dark gradient shade to improve title readability */
.fb.featured .box .shade{position:absolute; inset:0; pointer-events:none; z-index:1; background:linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0) 75%);}
.fb.featured .play{position:absolute; inset:0; display:grid; place-items:center; border:0; background:transparent; cursor:pointer;}
.video-badge { position:absolute !important; bottom:10px !important; left:10px !important; display:inline-block; width:auto !important; max-width:calc(100% - 60px); padding:6px 10px !important; background:rgba(0,0,0,0.6); color:#fff; font-size:14px; line-height:1.3; border-radius:.5rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; z-index:2; }
/* Remove background for Facebook title when requested (no-bg) */
.fb.featured .video-badge.no-bg{ background: transparent; padding: 0; }
/* Title row with icon + text */
.title-row{ display: inline-flex; align-items: center; gap: 8px; }
/* Brand chip wraps the icon for better readability on thumbnails */
.brand-chip{ width: 1.5rem; height: auto; border-radius: 50%; background: #1877F2; opacity: 0.85; display: inline-grid; place-items: center; }
.title-icon{ width: 16px; height: 16px; display:block; object-fit: contain; }
.fb.featured .share, .fb.featured .open{position:absolute; right:16px; bottom:10px; width:24px; height:24px; display:grid; place-items:center; border-radius:50%;  color:#fff; z-index:3; border:0;}

/* Classic slide-down embed */
.classic-embed .box{position:relative; aspect-ratio:16/9; background:#000; overflow:hidden;}
.classic-embed iframe{position:absolute; inset:0; width:100%; height:100%;}

/* Transition */
.slide-fade-enter-from,.slide-fade-leave-to{max-height:0; opacity:0; transform:translateY(-6px);}
.slide-fade-enter-active,.slide-fade-leave-active{transition:all .25s ease;}
.slide-fade-enter-to,.slide-fade-leave-from{max-height:1000px; opacity:1; transform:translateY(0);}

/* Close button */
.close-btn{position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4;}
.close-btn:hover{background:rgba(0,0,0,.6);}
</style>
